/* =============================================================================
   NORTHBORDERS · Giveaway · Interactions
   Loaded by the split-section pages (giveaway-hero, giveaway-prizes, …).
   Each section renders its own .nb-gv2 wrapper, so queries are document-scoped
   and rely on .nb-gv2-* / data-nb-gv2-* namespacing.
   Each module owns one concern.
   ============================================================================= */
(function () {
  'use strict';

  // The split-section architecture renders one <script src="giveaway.js"> tag
  // per section. The browser caches the file but still executes it for each
  // script tag, which would attach event listeners N times and toggle state
  // N times per click. Guard with a sentinel so we only wire things up once.
  if (window.__nbGv2Wired) return;
  window.__nbGv2Wired = true;

  if (!document.querySelector('.nb-gv2')) return;

  const $  = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const padTime = (v) => String(Math.max(0, v)).padStart(2, '0');
  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function readData() {
    const node = document.getElementById('nb-gv2-data');
    if (!node) return {};
    try { return JSON.parse(node.textContent); }
    catch (err) { console.warn('[nb-gv2] data parse failed', err); return {}; }
  }

  // -------- Reveal on scroll
  const Reveal = (() => {
    function init() {
      const targets = $$('.nb-gv2-reveal');
      if (targets.length === 0) return;
      if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
        targets.forEach(el => el.classList.add('is-in'));
        return;
      }
      const io = new IntersectionObserver((entries) => {
        for (const e of entries) {
          if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
        }
      }, { threshold: 0.1, rootMargin: '0px 0px -80px 0px' });
      targets.forEach(el => io.observe(el));
    }
    return { init };
  })();

  // -------- Countdown
  const Countdown = (() => {
    function init(data) {
      const target = Date.parse(data.drawClosesAt || '');
      if (!Number.isFinite(target)) return;
      const values = $$('[data-nb-gv2-countdown-value]');
      if (values.length === 0) return;
      function tick() {
        const remaining = Math.max(0, target - Date.now());
        const sec   = Math.floor(remaining / 1000);
        const days  = Math.floor(sec / 86400);
        const hours = Math.floor((sec % 86400) / 3600);
        const mins  = Math.floor((sec % 3600) / 60);
        const secs  = sec % 60;
        const map = { days: padTime(days), hours: padTime(hours), minutes: padTime(mins), seconds: padTime(secs) };
        for (const el of values) {
          const k = el.dataset.nbGv2CountdownValue;
          if (k in map) el.textContent = map[k];
        }
      }
      tick();
      const id = window.setInterval(tick, 1000);
      document.addEventListener('visibilitychange', () => { if (!document.hidden) tick(); });
      window.addEventListener('pagehide', () => window.clearInterval(id), { once: true });
    }
    return { init };
  })();

  // -------- Shop filters
  const Filters = (() => {
    function init() {
      const shop = $('[data-nb-gv2-shop]');
      if (!shop) return;
      const buttons = $$('[data-nb-gv2-filter]', shop);
      const cards   = $$('[data-nb-gv2-category]', shop);
      if (!buttons.length || !cards.length) return;
      buttons.forEach((btn) => {
        btn.addEventListener('click', () => {
          const cat = btn.dataset.nbGv2Filter;
          buttons.forEach((b) => {
            const active = b === btn;
            b.classList.toggle('is-active', active);
            b.setAttribute('aria-pressed', String(active));
          });
          cards.forEach((c) => {
            const show = cat === 'ALL' || c.dataset.nbGv2Category === cat;
            c.classList.toggle('nb-gv2-hidden', !show);
          });
        });
      });
    }
    return { init };
  })();

  // Billing toggle module removed: the members section now renders the
  // theme's `price-table` snippet, which carries its own toggle markup and
  // is hydrated by the theme's existing price-table JS.

  // -------- FAQ accordion: animated open/close on <details>
  // Native <details> snaps non-summary children to display:none on close
  // (killing any transition), so we drive max-height transitions manually
  // and flip the [open] attribute IN SYNC with the transition start so the
  // icon rotation runs alongside the height collapse.
  const Faq = (() => {
    const DUR = 250; // keep in sync with --nb-gv2-dur-base

    function setInstant(content, value) {
      // Apply value without triggering the CSS transition.
      content.style.transition = 'none';
      content.style.maxHeight = value;
      // Flush, then restore transition in next frame so subsequent changes animate.
      // eslint-disable-next-line no-unused-expressions
      content.offsetHeight;
    }

    function restoreTransition(content) {
      content.style.transition = '';
      // eslint-disable-next-line no-unused-expressions
      content.offsetHeight;
    }

    function expand(row) {
      const content = row.querySelector('.nb-gv2-faq__a');
      if (!content) return;
      row.open = true;                         // icon starts rotating now
      setInstant(content, '0px');              // anchor at 0
      restoreTransition(content);
      requestAnimationFrame(() => {
        content.style.maxHeight = content.scrollHeight + 'px';
      });
      window.setTimeout(() => {                // release after transition
        if (row.open) content.style.maxHeight = 'none';
      }, DUR + 50);
    }

    function collapse(row) {
      const content = row.querySelector('.nb-gv2-faq__a');
      if (!content) return;
      // Anchor at current explicit height (so the transition has a FROM value)
      setInstant(content, content.scrollHeight + 'px');
      restoreTransition(content);
      requestAnimationFrame(() => {
        content.style.maxHeight = '0px';
        row.open = false;                      // icon rotates back in sync
      });
    }

    function init() {
      const rows = $$('[data-nb-gv2-faq-row]');
      if (!rows.length) return;

      rows.forEach((row) => {
        const summary = row.querySelector('summary');
        const content = row.querySelector('.nb-gv2-faq__a');
        if (!summary || !content) return;

        // Initial state for rows rendered with [open]
        if (row.open) content.style.maxHeight = 'none';

        summary.addEventListener('click', (e) => {
          e.preventDefault();
          if (row.open) {
            collapse(row);
          } else {
            rows.forEach((other) => { if (other !== row && other.open) collapse(other); });
            expand(row);
          }
        });
      });

      // Recompute open row heights on resize
      let resizeTimer;
      window.addEventListener('resize', () => {
        window.clearTimeout(resizeTimer);
        resizeTimer = window.setTimeout(() => {
          rows.forEach((row) => {
            if (!row.open) return;
            const content = row.querySelector('.nb-gv2-faq__a');
            if (content) content.style.maxHeight = 'none';
          });
        }, 120);
      }, { passive: true });
    }
    return { init };
  })();

  // -------- Video lightbox
  const Video = (() => {
    function init() {
      const trigger = $('[data-nb-gv2-video-trigger]');
      const frame   = $('[data-nb-gv2-video]');
      const tmpl    = document.getElementById('nb-gv2-video-template');
      if (!trigger || !frame || !tmpl) return;
      trigger.addEventListener('click', () => {
        const id = frame.dataset.nbGv2VideoId;
        if (!id) return;
        const iframe = tmpl.content.firstElementChild.cloneNode(true);
        iframe.src = 'https://www.youtube.com/embed/' + id + '?autoplay=1&rel=0';
        frame.replaceChildren(iframe);
      });
    }
    return { init };
  })();

  // -------- Drag-to-scroll for horizontal rails
  const DragScroll = (() => {
    function bind(el) {
      let isDown = false;
      let startX = 0;
      let startScroll = 0;
      let moved = 0;

      el.addEventListener('mousedown', (e) => {
        // Only left-click
        if (e.button !== 0) return;
        isDown = true;
        moved = 0;
        startX = e.pageX;
        startScroll = el.scrollLeft;
        el.classList.add('is-dragging');
      });

      const release = () => {
        if (!isDown) return;
        isDown = false;
        el.classList.remove('is-dragging');
      };

      el.addEventListener('mouseleave', release);
      window.addEventListener('mouseup', release);

      el.addEventListener('mousemove', (e) => {
        if (!isDown) return;
        const dx = e.pageX - startX;
        moved = Math.max(moved, Math.abs(dx));
        el.scrollLeft = startScroll - dx;
      });

      // Suppress click-through on child anchors/buttons if the user dragged
      el.addEventListener('click', (e) => {
        if (moved > 5) {
          e.preventDefault();
          e.stopPropagation();
        }
      }, true);

      // Native horizontal scroll on vertical wheel (so a regular mouse can scroll it)
      el.addEventListener('wheel', (e) => {
        if (e.deltaY === 0) return;
        // Only convert if there's actually horizontal overflow
        if (el.scrollWidth <= el.clientWidth) return;
        el.scrollLeft += e.deltaY;
        e.preventDefault();
      }, { passive: false });
    }

    function init() {
      $$('.nb-gv2-winners__strip').forEach(bind);
    }
    return { init };
  })();

  // -------- Calculator
  const Calculator = (() => {
    function init() {
      const sec = $('[data-nb-gv2-calc]');
      if (!sec) return;
      const total    = sec.querySelector('[data-nb-gv2-calc-total]');
      const copy     = sec.querySelector('[data-nb-gv2-calc-copy]');
      const idleCopy = copy ? (copy.dataset.nbGv2CalcCopyIdle || copy.textContent.trim()) : '';
      const submit   = sec.querySelector('[data-nb-gv2-calc-submit]');
      const errorEl  = sec.querySelector('[data-nb-gv2-calc-error]');
      const products = $$('[data-nb-gv2-calc-product]', sec);
      const selected = new Set();
      let lastValue  = 0;
      let busy       = false;

      function renderTotal(value) {
        if (!total) return;
        if (prefersReducedMotion() || value === 0 || Math.abs(value - lastValue) > 5000) {
          total.textContent = value.toLocaleString();
        } else {
          const from = Number((total.textContent || '').replace(/[^0-9]/g, '')) || 0;
          const start = performance.now();
          const dur = 450;
          const easeOut = (t) => 1 - Math.pow(1 - t, 3);
          function step(now) {
            const t = Math.min(1, (now - start) / dur);
            const v = Math.round(from + (value - from) * easeOut(t));
            total.textContent = v.toLocaleString();
            if (t < 1) requestAnimationFrame(step);
            else total.textContent = value.toLocaleString();
          }
          requestAnimationFrame(step);
        }
        lastValue = value;
      }
      function renderCopy(value) {
        if (!copy) return;
        if (value <= 0) {
          copy.textContent = idleCopy;
          return;
        }
        copy.replaceChildren();
      }
      function recalc() {
        let sum = 0;
        for (const btn of products) {
          if (selected.has(btn.dataset.nbGv2CalcProduct)) {
            sum += Number(btn.dataset.nbGv2CalcEntries) || 0;
          }
        }
        renderTotal(sum);
        renderCopy(sum);
        if (submit && !busy) submit.disabled = sum === 0;
        for (const btn of products) {
          const sel = selected.has(btn.dataset.nbGv2CalcProduct);
          btn.classList.toggle('is-selected', sel);
          btn.setAttribute('aria-pressed', String(sel));
        }
      }
      function showError(msg) {
        if (!errorEl) return;
        errorEl.textContent = msg;
        errorEl.hidden = false;
        submit.classList.add('has-error');
      }
      function clearError() {
        if (!errorEl) return;
        errorEl.hidden = true;
        errorEl.textContent = '';
        submit.classList.remove('has-error');
      }
      async function handleSubmit() {
        if (busy || !submit) return;
        clearError();
        const items = [];
        for (const btn of products) {
          if (!selected.has(btn.dataset.nbGv2CalcProduct)) continue;
          const id = Number(btn.dataset.nbGv2CalcVariant);
          if (!id) continue;
          const properties = {};
          const compare = btn.dataset.nbGv2CalcCompare;
          const bonus   = btn.dataset.nbGv2CalcBonus;
          // Match snippets/giveaway-line-item-properties.liquid:
          // compare_at + bonus → both keys; else → entries as "Giveaway Entries"
          if (compare && bonus) {
            properties['Giveaway Entries'] = compare;
            properties['Bonus Entries']    = bonus;
          } else {
            properties['Giveaway Entries'] = btn.dataset.nbGv2CalcEntries;
          }
          items.push({ id, quantity: 1, properties });
        }
        if (!items.length) return;

        busy = true;
        submit.disabled = true;
        submit.classList.add('is-loading');
        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ items })
          });
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.description || err.message || 'Something went wrong.');
          }
          // Hand off to the theme's own cart pipeline:
          //   `theme:cart:refresh`  → re-fetches /cart?section_id=api-cart-items
          //                            and rebuilds the drawer's items DOM
          //   `theme:product:added` → opens the drawer if it isn't already
          // Refresh first so the drawer is current the moment it opens.
          document.dispatchEvent(new CustomEvent('theme:cart:refresh',  { bubbles: true }));
          document.dispatchEvent(new CustomEvent('theme:product:added', { bubbles: true }));
          // Reset calculator atomically — drawer popping open is the success
          // signal, so we don't need an is-added flourish (which is what forces
          // a timer in the theme's inline ATC flow). `recalc()` will disable
          // the submit again now that no chips are selected.
          submit.classList.remove('is-loading');
          selected.clear();
          busy = false;
          recalc();
        } catch (e) {
          submit.classList.remove('is-loading');
          showError(e.message || 'Could not add to cart.');
          busy = false;
          submit.disabled = false;
        }
      }
      products.forEach((btn) => {
        btn.addEventListener('click', () => {
          const id = btn.dataset.nbGv2CalcProduct;
          if (!id) return;
          if (selected.has(id)) selected.delete(id);
          else selected.add(id);
          recalc();
        });
      });
      if (submit) submit.addEventListener('click', handleSubmit);
      recalc();
    }
    return { init };
  })();

  function init() {
    const data = readData();
    Reveal.init();
    Countdown.init(data);
    Filters.init();
    Faq.init();
    Video.init();
    Calculator.init();
    DragScroll.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
